import { z } from "zod";
import { CampaignManagedGroupSchema } from "./ai-managed-group";
import {
  AiPolicyBlockedReasonV2Schema,
  AiPolicyManagementV2Schema,
  AiPolicyRunGatesV2Schema,
  AiPolicySourceV2Schema,
  BusinessGoalV1Schema,
} from "./ai-policy";
import { budgetHealthSchema } from "./budget-health";
import { reportingDateRangeQueryShape, validateReportingDateRange } from "./reporting";

/** Campaign type */
export const CampaignTypeSchema = z.enum(["sp", "sb", "sd"]);
export type CampaignType = z.infer<typeof CampaignTypeSchema>;

/** Campaign status (from Amazon) */
export const CampaignStatusSchema = z.enum(["enabled", "paused", "archived"]);
export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

/** Keyword match type */
export const MatchTypeSchema = z.enum(["exact", "phrase", "broad"]);
export type MatchType = z.infer<typeof MatchTypeSchema>;

/** Keyword tag source */
export const TagSourceSchema = z.enum(["user", "ai"]);
export type TagSource = z.infer<typeof TagSourceSchema>;

// ── Target discriminators (ADP-39 data model + ADP-44 read layer) ──────────

/** Sponsored Products target kind — one row per Amazon `/targets/export` kind. */
export const TargetTypeSchema = z.enum(["keyword", "auto", "product", "product_category", "theme"]);
export type TargetType = z.infer<typeof TargetTypeSchema>;

/** Campaign targeting type (Amazon `targetingType`) — manual vs auto. */
export const TargetingTypeSchema = z.enum(["manual", "auto"]);
export type TargetingType = z.infer<typeof TargetingTypeSchema>;

/** Where a target lives in the SP hierarchy — campaign-level or ad-group-level. */
export const TargetLevelSchema = z.enum(["campaign", "ad_group"]);
export type TargetLevel = z.infer<typeof TargetLevelSchema>;

/** Auto-targeting expression (Amazon `expressionType`) — search/close-match kinds. */
export const ExpressionTypeSchema = z.enum([
  "close_match",
  "loose_match",
  "substitutes",
  "complements",
]);
export type ExpressionType = z.infer<typeof ExpressionTypeSchema>;

/** Human-readable names for auto-targeting expressions (D2). */
export const EXPRESSION_TYPE_LABELS: Record<ExpressionType, string> = {
  close_match: "Close match",
  loose_match: "Loose match",
  substitutes: "Substitutes",
  complements: "Complements",
};

/** Map an auto-targeting expression enum to its human-readable label (or null). */
export function expressionTypeLabel(
  expressionType: ExpressionType | null | undefined,
): string | null {
  if (!expressionType) return null;
  return EXPRESSION_TYPE_LABELS[expressionType] ?? null;
}

/** Minimal descriptor needed to derive a one-line display label for any target. */
export interface TargetLabelInput {
  targetType: TargetType;
  keywordText?: string | null;
  expressionType?: ExpressionType | null;
  asin?: string | null;
  targetDetails?: Record<string, unknown> | null;
}

function readStringDetail(
  details: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const value = details?.[key];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

/**
 * Derive a non-empty human-readable label for a target of any type (D2).
 *
 * keyword → keywordText; auto → expression human name; product → ASIN;
 * product_category → targetDetails.productCategoryName; theme → targetDetails
 * theme name/id. Falls back to the targetType uppercased so the label is never
 * empty (shared schema marks `label` required, the UI shows this fallback).
 */
export function deriveTargetLabel(target: TargetLabelInput): string {
  const fallback = target.targetType.toUpperCase();
  switch (target.targetType) {
    case "keyword":
      return target.keywordText?.trim() || fallback;
    case "auto":
      return expressionTypeLabel(target.expressionType) ?? fallback;
    case "product":
      return target.asin?.trim() || fallback;
    case "product_category":
      return readStringDetail(target.targetDetails, "productCategoryName") ?? fallback;
    case "theme":
      return (
        readStringDetail(target.targetDetails, "name") ??
        readStringDetail(target.targetDetails, "themeName") ??
        readStringDetail(target.targetDetails, "id") ??
        readStringDetail(target.targetDetails, "themeId") ??
        fallback
      );
  }
}

export const campaignDetailRangeSchema = z.enum(["yesterday", "7d", "14d", "30d"]);
export type CampaignDetailRange = z.infer<typeof campaignDetailRangeSchema>;

export const campaignDetailQuerySchema = z
  .object({
    range: campaignDetailRangeSchema.catch("30d"),
    ...reportingDateRangeQueryShape,
  })
  .superRefine(validateReportingDateRange);
export type CampaignDetailQuery = z.infer<typeof campaignDetailQuerySchema>;

export const metricSnapshotSchema = z.object({
  impressions: z.number(),
  clicks: z.number(),
  spend: z.number(),
  sales: z.number(),
  orders: z.number(),
  units: z.number(),
  ctr: z.number(),
  cvr: z.number(),
  acos: z.number(),
  roas: z.number(),
  cpc: z.number(),
  cpa: z.number(),
});
export type MetricSnapshot = z.infer<typeof metricSnapshotSchema>;

export const campaignBatchAiManagementApiSchema = z.object({
  campaign_ids: z
    .array(z.string().uuid())
    .min(1)
    .transform((ids) => Array.from(new Set(ids))),
  ai_managed: z.boolean(),
});
export type CampaignBatchAiManagementApi = z.infer<typeof campaignBatchAiManagementApiSchema>;

export const campaignBatchAiManagementResponseSchema = z.object({
  updated: z.number(),
  aiManaged: z.boolean(),
});
export type CampaignBatchAiManagementResponse = z.infer<
  typeof campaignBatchAiManagementResponseSchema
>;

/**
 * ADP-49: per-placement breakdown (campaign×placement grain). `spendShare` =
 * placement spend / total campaign spend (sums to ~1 across placements).
 */
export const placementBreakdownSchema = z.object({
  placement: z.string(),
  spend: z.number(),
  sales: z.number(),
  acos: z.number(),
  spendShare: z.number(),
});
export type PlacementBreakdown = z.infer<typeof placementBreakdownSchema>;

export const campaignDetailCampaignSchema = z.object({
  shopId: z.string().uuid().nullable().default(null),
  id: z.string(),
  name: z.string(),
  campaignType: CampaignTypeSchema,
  status: CampaignStatusSchema,
  // manual/auto (Amazon `targetingType`); nullable for SB/SD or pre-ADP-39 rows.
  targetingType: TargetingTypeSchema.nullable(),
  startDate: z.string().nullable().default(null),
  endDate: z.string().nullable().default(null),
  portfolioId: z.string().uuid().nullable().default(null),
  portfolioName: z.string().nullable().default(null),
  biddingStrategy: z.string().nullable().default(null),
  dailyBudget: z.number().nullable(),
  // Monthly budgets cannot be edited as daily budgets; null when Amazon did not report one.
  budgetType: z.enum(["daily", "monthly"]).nullable().default(null),
  aiManaged: z.boolean(),
  managedGroup: CampaignManagedGroupSchema.nullable().default(null),
  aiPolicySummary: z
    .object({
      effectiveEnabled: z.boolean(),
      management: AiPolicyManagementV2Schema,
      gates: AiPolicyRunGatesV2Schema,
      businessGoal: BusinessGoalV1Schema,
      businessGoalSource: AiPolicySourceV2Schema,
      targetAcos: z.number(),
      targetAcosSource: AiPolicySourceV2Schema,
      approvalRiskThreshold: z.enum(["all", "medium_plus", "high_plus", "critical_only", "none"]),
      approvalRiskThresholdSource: AiPolicySourceV2Schema,
      dryRun: z.boolean(),
      dryRunSource: AiPolicySourceV2Schema,
      disabledReason: AiPolicyBlockedReasonV2Schema.nullable(),
    })
    .nullable(),
  metrics: metricSnapshotSchema,
  // ADP-47: today's spend for the budget-pacing block (range-independent).
  todaySpend: z.number(),
  // ADP-89: derived budget health (today's spend vs daily budget), assembled
  // server-side so the pacing block doesn't re-derive it from raw fields.
  budgetHealth: budgetHealthSchema,
  // ADP-49: placement breakdown for the selected range (campaign×placement grain).
  placements: z.array(placementBreakdownSchema),
  lastAiRun: z.string().nullable(),
  pendingApprovals: z.number(),
});
export type CampaignDetailCampaign = z.infer<typeof campaignDetailCampaignSchema>;

export const campaignTrendPointSchema = metricSnapshotSchema.extend({
  date: z.string(),
});
export type CampaignTrendPoint = z.infer<typeof campaignTrendPointSchema>;

export const keywordTagSchema = z.object({
  tag: z.string(),
  source: TagSourceSchema,
});
export type KeywordTagValue = z.infer<typeof keywordTagSchema>;

/** Advertised (same-SKU) vs other-product 7d attribution split. */
export const skuBreakdownSchema = z.object({
  advertisedOrders: z.number(),
  advertisedUnits: z.number(),
  advertisedSales: z.number(),
  otherOrders: z.number(),
  otherUnits: z.number(),
  otherSales: z.number(),
});
export type SkuBreakdown = z.infer<typeof skuBreakdownSchema>;

/**
 * A positive target of any type in a campaign detail (D2).
 *
 * `label` is API-derived and non-empty; the type-specific discriminators
 * (`keywordMatchType`/`expressionType`/`asin`) are present only for their type.
 */
export const campaignTargetSchema = z.object({
  id: z.string(),
  targetType: TargetTypeSchema,
  label: z.string().min(1),
  targetLevel: TargetLevelSchema,
  adGroup: z.string(),
  // Campaign-level lists span ad groups; legacy ad-group-scoped responses omit it.
  adGroupId: z.string().nullable().default(null),
  bid: z.number().nullable(),
  status: CampaignStatusSchema,
  // Amazon delivery status as synced (free text, e.g. "DELIVERING"); null when unknown.
  deliveryStatus: z.string().nullable().default(null),
  amazonCreatedAt: z.string().nullable().default(null),
  tags: z.array(keywordTagSchema),
  // At least one report row in the requested range; false = "not captured", not a real 0.
  hasMetrics: z.boolean().default(false),
  metrics: metricSnapshotSchema,
  skuBreakdown: skuBreakdownSchema.nullable().default(null),
  // Type-specific discriminators (present only for their targetType):
  keywordMatchType: MatchTypeSchema.nullable().optional(), // keyword only
  expressionType: ExpressionTypeSchema.nullable().optional(), // auto only
  asin: z.string().nullable().optional(), // product only
  aiRecommendation: z.enum(["increase", "decrease", "keep", "observe"]).nullable(),
  confidence: z.enum(["high", "medium", "low"]).nullable(),
  risk: z.enum(["low", "medium", "high", "critical"]).nullable(),
});
export type CampaignTarget = z.infer<typeof campaignTargetSchema>;

/**
 * A negative target — never in the `targets` array, surfaced separately (D1).
 * Negatives carry no metrics/bid (they don't spend).
 */
export const campaignNegativeTargetSchema = z.object({
  id: z.string(),
  targetType: TargetTypeSchema,
  label: z.string().min(1),
  targetLevel: TargetLevelSchema,
  adGroupId: z.string().nullable().default(null), // null for campaign-level negatives
  adGroup: z.string().nullable(), // null for campaign-level negatives
  keywordMatchType: MatchTypeSchema.nullable().default(null), // keyword negatives only
  status: CampaignStatusSchema,
  amazonCreatedAt: z.string().nullable().default(null),
});
export type CampaignNegativeTarget = z.infer<typeof campaignNegativeTargetSchema>;

export const campaignNegativesResponseSchema = z.object({
  currency: z.string().nullable().default(null),
  connected: z.boolean(),
  negatives: z.array(campaignNegativeTargetSchema),
});
export type CampaignNegativesResponse = z.infer<typeof campaignNegativesResponseSchema>;

export const campaignSearchTermSchema = z
  .object({
    id: z.string(),
    targetId: z.string().nullable(),
    adGroupId: z.string(),
    // Campaign-level lists span ad groups; legacy ad-group-scoped responses omit it.
    adGroup: z.string().nullable().default(null),
    attributionStatus: z.enum([
      "resolved_by_id",
      "resolved_by_unique_expression",
      "no_matching_target",
      "orphan_amazon_target_id",
      "missing_expression",
      "unknown_expression",
      "ambiguous_fallback",
      "legacy_unverified",
    ]),
    text: z.string(),
    // Derived display label of the source target (keyword/auto/product); fallback
    // rather than empty when the source has no label.
    matchedTarget: z.string().nullable(),
    // Mutually exclusive: keyword-origin → keywordMatchType set; auto-origin →
    // expressionType set. The other is null.
    keywordMatchType: MatchTypeSchema.nullable(),
    expressionType: ExpressionTypeSchema.nullable(),
    metrics: metricSnapshotSchema,
    aiSuggestion: z.enum(["harvest", "negative", "observe"]),
    approvalStatus: z.enum(["none", "pending", "approved", "rejected"]),
  })
  .refine((term) => term.keywordMatchType == null || term.expressionType == null, {
    message: "keywordMatchType and expressionType are mutually exclusive",
  });
export type CampaignSearchTerm = z.infer<typeof campaignSearchTermSchema>;

export const campaignSearchTermsResponseSchema = z.object({
  currency: z.string().nullable().default(null),
  connected: z.boolean(),
  searchTerms: z.array(campaignSearchTermSchema),
});
export type CampaignSearchTermsResponse = z.infer<typeof campaignSearchTermsResponseSchema>;

export const campaignAiActionSchema = z.object({
  id: z.string(),
  actionType: z.string(),
  target: z.string(),
  status: z.enum([
    "pending_approval",
    "approved",
    "rejected",
    "proposed",
    "superseded",
    "executing",
    "executed",
    "failed",
    "skipped",
  ]),
  reason: z.string(),
  createdAt: z.string(),
});
export type CampaignAiAction = z.infer<typeof campaignAiActionSchema>;

export const campaignAiRunSchema = z.object({
  id: z.string(),
  status: z.enum(["running", "suspended", "success", "partial", "failed"]),
  triggerType: z.enum(["cron", "manual"]),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  candidateActions: z.number().int().nonnegative(),
  autoExecuted: z.number().int().nonnegative(),
  approvalCreated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failureReason: z.string().nullable(),
});
export type CampaignAiRun = z.infer<typeof campaignAiRunSchema>;

export const campaignDetailResponseSchema = z.object({
  connected: z.boolean(),
  range: campaignDetailRangeSchema,
  campaign: campaignDetailCampaignSchema.nullable(),
  // ADP-48: Targets + Search Terms moved to the Ad Group detail endpoint
  // (scoped per ad group). The overview keeps only campaign-level negatives.
  negatives: z.array(campaignNegativeTargetSchema),
  aiActions: z.array(campaignAiActionSchema),
  aiRuns: z.array(campaignAiRunSchema).default([]),
});
export type CampaignDetailResponse = z.infer<typeof campaignDetailResponseSchema>;

export const targetTagsUpdateSchema = z.object({
  tags: z.array(z.string().trim().min(1).max(40)).max(12),
});
export type TargetTagsUpdate = z.infer<typeof targetTagsUpdateSchema>;

// ── Ad Group navigation (ADP-46) + content (ADP-48) ────────────────────────
//
// Ad Group is a navigable level under Campaign (ADR-0001). ADP-46 added the
// list + detail-skeleton endpoints; ADP-48 fills the detail with the ad group's
// own Targets / Negatives / Search Terms (scoped per ad group), removing that
// content from the campaign overview.

const adGroupIdentitySchema = z.object({
  id: z.string(),
  name: z.string(),
  defaultBid: z.number().nullable(),
  status: CampaignStatusSchema,
});

/** Ad Group summary row on the campaign overview's Ad Groups list. */
export const adGroupSummarySchema = adGroupIdentitySchema.extend({
  // At least one report row in the requested range; not a guarantee of complete daily coverage.
  hasMetrics: z.boolean().default(false),
  aiManaged: z.boolean(),
  targetCount: z.number(),
  metrics: metricSnapshotSchema,
  // Advertised (same-SKU) vs other-product 7d attribution; null when not captured for the range.
  skuBreakdown: skuBreakdownSchema.nullable().default(null),
});
export type AdGroupSummary = z.infer<typeof adGroupSummarySchema>;

/** Ad Group detail — adds campaignId for breadcrumb/back-link context. */
export const adGroupDetailSchema = adGroupIdentitySchema.extend({
  campaignId: z.string(),
  campaignName: z.string(),
  metrics: metricSnapshotSchema,
});
export type AdGroupDetail = z.infer<typeof adGroupDetailSchema>;

export const campaignTargetsResponseSchema = z.object({
  currency: z.string().nullable().default(null),
  connected: z.boolean(),
  targets: z.array(campaignTargetSchema),
});
export type CampaignTargetsResponse = z.infer<typeof campaignTargetsResponseSchema>;

export const campaignAdGroupsResponseSchema = z.object({
  currency: z.string().nullable().default(null),
  connected: z.boolean(),
  adGroups: z.array(adGroupSummarySchema),
});
export type CampaignAdGroupsResponse = z.infer<typeof campaignAdGroupsResponseSchema>;

export const adGroupDetailResponseSchema = z.object({
  connected: z.boolean(),
  adGroup: adGroupDetailSchema.nullable(),
  // ADP-48: scoped to this ad group. Negatives are ad-group-level only
  // (campaign-level negatives stay on the campaign overview).
  targets: z.array(campaignTargetSchema),
  negatives: z.array(campaignNegativeTargetSchema),
  searchTerms: z.array(campaignSearchTermSchema),
  trend: z.array(campaignTrendPointSchema).default([]),
  advertisedProductCoverage: z
    .object({
      status: z.enum(["unavailable", "partial", "complete"]),
      coveredDays: z.number().int().nonnegative(),
      totalDays: z.number().int().nonnegative(),
    })
    .default({ status: "unavailable", coveredDays: 0, totalDays: 0 }),
  advertisedProducts: z
    .array(
      z.object({
        id: z.string(),
        asin: z.string(),
        title: z.string().nullable(),
        imageUrl: z.string().nullable(),
        status: CampaignStatusSchema,
        metrics: metricSnapshotSchema.extend({ salesShare: z.number().min(0).max(1) }).nullable(),
      }),
    )
    .default([]),
  bidHistory: z
    .array(
      z.object({
        id: z.string(),
        runId: z.string(),
        targetId: z.string().nullable(),
        targetLabel: z.string(),
        oldBid: z.number().nullable(),
        newBid: z.number().nullable(),
        source: z.enum(["ai_auto", "ai_approved"]),
        status: z.enum(["executed", "failed"]),
        risk: z.enum(["low", "medium", "high", "critical"]),
        reason: z.string(),
        createdAt: z.string(),
      }),
    )
    .default([]),
});
export type AdGroupDetailResponse = z.infer<typeof adGroupDetailResponseSchema>;
